import { SCREENS } from '#constants';
import { useMediaQueries } from '@react-hook/media-query';

type BreakPointKeys = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

type QueryMap = {
  [K in BreakPointKeys]: {
    [key in `is${Capitalize<K>}` | `is${Capitalize<K>}Only`]: string;
  };
};

type QueryMapOb<T extends string, K = string> = {
  [key in `is${Capitalize<T>}` | `is${Capitalize<T>}Only`]: K;
};

type QueryMapRet<
  T extends string[] | undefined = undefined,
  K = string,
> = T extends string[]
  ? QueryMapOb<T[number], K>
  : T extends undefined
    ? QueryMapOb<BreakPointKeys, K>
    : never;

function createQueryUp(bp: string | number) {
  return `(min-width: ${typeof bp === 'string' ? bp : `${bp}px`})`;
}

// The maximum value is reduced by 0.02px to work around the limitations of
// `min-` and `max-` prefixes and viewports with fractional widths.
// See https://www.w3.org/TR/mediaqueries-4/#mq-min-max
// Uses 0.02px rather than 0.01px to work around a current rounding bug in Safari.
// See https://bugs.webkit.org/show_bug.cgi?id=178261

const getCorrectMaxValue = <T extends number | string>(value: T) => {
  if (typeof value === 'number') {
    return value - 0.02;
  }

  const num = parseFloat(value);
  if (!isNaN(num)) {
    return `${num - 0.02}${value.split(String(num))[1]}`;
  }

  if (import.meta.env.DEV) console.error(`Cannot covert '${value}' to rem`);
};

function createBetweenQuery(
  smallerBreakPoint: number | string,
  largerBreakPoint: number | string | undefined,
) {
  smallerBreakPoint =
    typeof smallerBreakPoint === 'string'
      ? smallerBreakPoint
      : `${smallerBreakPoint}px`;

  if (!largerBreakPoint) {
    return `(min-width: ${smallerBreakPoint})`;
  }

  let correctedMaxValue = getCorrectMaxValue(largerBreakPoint);

  correctedMaxValue =
    typeof correctedMaxValue === 'string'
      ? correctedMaxValue
      : `${correctedMaxValue}px`;

  return `(min-width: ${smallerBreakPoint}) and (max-width: ${correctedMaxValue})`;
}

export const makeUseMatchMediaHook = <T extends Record<string, number>>(
  breakPointsOverride?: T,
) => {
  const breakPoints = {
    ...SCREENS,
    ...breakPointsOverride,
  };

  const queryKeys = Object.keys(breakPoints) as BreakPointKeys[];

  const queryMap = Object.fromEntries(
    Object.entries(breakPoints).map(([key, value], index) => {
      const transformedKey = `${key[0].toUpperCase()}${key.slice(1)}`;
      return [
        key,
        {
          [`is${transformedKey}Only`]: createBetweenQuery(
            value,
            breakPoints[queryKeys[index + 1]],
          ),
          [`is${transformedKey}`]: createQueryUp(value),
        },
      ];
    }),
  ) as QueryMap;

  const getQueryMap = <T extends string[]>(keys?: T) => {
    if (!keys || !keys.length) {
      return Object.entries(queryMap).reduce(
        (r, [, value]) => Object.assign(r, value),
        {},
      ) as QueryMapRet<T>;
    }
    return Object.entries(queryMap)
      .filter(([key]) => keys.includes(key as keyof QueryMap))
      .reduce((r, [, value]) => Object.assign(r, value), {}) as QueryMapRet<T>;
  };

  /**
   * Hook that can be used to determine window size
   * @example
   * const { isDesktop, isDesktopOnly } = useMatchMedia(['desktop']);
   * @example
   * const { isMobile, isMobileOnly, isLaptop, ...., isWide, isWideOnly } = useMatchMedia();
   * @param {array} mediaKeys - Array of mediaKeys to be matched.
   * If undefined then it returns matches for all mediaKeys
   * @returns {object} matches - Object where keys are either 'is(MediaKey)' or 'is(MediaKey)Only' and value is a boolean indicating if it matches or not.
   * <pre>
   * isDesktop -> Matches 1366px and up.
   * isDesktopOnly -> Matches only from 1366px to 1920px
   * </pre>
   */
  return <T extends BreakPointKeys[] | undefined = undefined>(
    mediaKeys?: T,
  ) => {
    const { matches } = useMediaQueries(getQueryMap(mediaKeys)) as unknown as {
      matches: QueryMapRet<T, boolean>;
    };
    return matches;
  };
};
